import StreamService from "@/services/StreamService";

/**
 * StreamCache Module
 * Caches stream-specific data like metadata, scheduler config, etc.
 * Automatically cleared when navigating away from a stream
 */

const defaultState = () => ({
  metadata: {}, // { streamId: { data, loading, loaded } }
  schedulerConfig: {}, // { streamId: { data, loading, loaded } }
});

const state = defaultState();

const mutations = {
  SET_METADATA_LOADING(state, { streamId, isLoading }) {
    if (!state.metadata[streamId]) {
      state.metadata[streamId] = { data: null, loading: false, loaded: false };
    }
    state.metadata[streamId].loading = isLoading;
  },
  SET_METADATA(state, { streamId, metadata }) {
    state.metadata[streamId] = {
      data: metadata,
      loading: false,
      loaded: true,
    };
  },
  SET_SCHEDULER_CONFIG_LOADING(state, { streamId, isLoading }) {
    if (!state.schedulerConfig[streamId]) {
      state.schedulerConfig[streamId] = { data: null, loading: false, loaded: false };
    }
    state.schedulerConfig[streamId].loading = isLoading;
  },
  SET_SCHEDULER_CONFIG(state, { streamId, config }) {
    state.schedulerConfig[streamId] = {
      data: config,
      loading: false,
      loaded: true,
    };
  },
  CLEAR_STREAM_CACHE(state, streamId) {
    if (streamId) {
      delete state.metadata[streamId];
      delete state.schedulerConfig[streamId];
    } else {
      state.metadata = {};
      state.schedulerConfig = {};
    }
  },
};

const actions = {
  async getMetadata({ commit, state }, { streamId, videoId = false }) {
    const cacheKey = streamId;
    
    // If already loaded, return from cache
    if (state.metadata[cacheKey]?.loaded) {
      return state.metadata[cacheKey].data;
    }
    
    // If currently loading, wait for it
    if (state.metadata[cacheKey]?.loading) {
      return new Promise((resolve) => {
        const checkLoading = setInterval(() => {
          if (!state.metadata[cacheKey]?.loading) {
            clearInterval(checkLoading);
            resolve(state.metadata[cacheKey]?.data);
          }
        }, 50);
      });
    }
    
    commit("SET_METADATA_LOADING", { streamId, isLoading: true });
    try {
      const metadata = await StreamService.getStreamMetadata(streamId, videoId);
      commit("SET_METADATA", { streamId, metadata });
      return metadata;
    } catch (error) {
      commit("SET_METADATA_LOADING", { streamId, isLoading: false });
      throw error;
    }
  },
  
  async getSchedulerConfig({ commit, state }, streamId) {
    // If already loaded, return from cache
    if (state.schedulerConfig[streamId]?.loaded) {
      return state.schedulerConfig[streamId].data;
    }
    
    // If currently loading, wait for it
    if (state.schedulerConfig[streamId]?.loading) {
      return new Promise((resolve) => {
        const checkLoading = setInterval(() => {
          if (!state.schedulerConfig[streamId]?.loading) {
            clearInterval(checkLoading);
            resolve(state.schedulerConfig[streamId]?.data);
          }
        }, 50);
      });
    }
    
    commit("SET_SCHEDULER_CONFIG_LOADING", { streamId, isLoading: true });
    try {
      const config = await StreamService.getStreamScheduleSettings(streamId);
      commit("SET_SCHEDULER_CONFIG", { streamId, config });
      return config;
    } catch (error) {
      commit("SET_SCHEDULER_CONFIG_LOADING", { streamId, isLoading: false });
      throw error;
    }
  },
  
  clearStreamCache({ commit }, streamId) {
    commit("CLEAR_STREAM_CACHE", streamId);
  },
};

const getters = {
  getMetadataByStreamId: (state) => (streamId) => {
    return state.metadata[streamId]?.data || null;
  },
  getSchedulerConfigByStreamId: (state) => (streamId) => {
    return state.schedulerConfig[streamId]?.data || null;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
